import React from 'react';
import { FiFileText } from 'react-icons/fi';

type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | { [key: string]: JsonValue };

interface MetaDataTableProps {
  metaData: string | null;
}

const EmptyMetaDataState: React.FC = () => (
  <div className="py-8 flex flex-col items-center gap-2 text-center">
    <div className="w-10 h-10 rounded-full bg-default-100 flex items-center justify-center">
      <FiFileText className="w-5 h-5 text-default-400" />
    </div>
    <p className="text-sm font-medium text-foreground">No metadata</p>
    <p className="text-sm text-default-500">
      This transmission event has no metadata attached.
    </p>
  </div>
);

const renderValue = (value: JsonValue) => {
  if (typeof value === 'object' && value !== null) {
    // Nested objects/arrays fall back to pretty-printed JSON
    return (
      <pre className="whitespace-pre-wrap break-all font-mono text-xs text-default-600 m-0">
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  }

  return String(value);
};

export const MetaDataTable: React.FC<MetaDataTableProps> = ({ metaData }) => {
  if (!metaData) {
    return <EmptyMetaDataState />;
  }

  let parsedData: JsonValue;
  try {
    parsedData = JSON.parse(metaData);
  } catch {
    return (
      <div className="p-3 bg-default-100 dark:bg-default-50 rounded-lg">
        <p className="text-sm text-default-600 break-all">{metaData}</p>
      </div>
    );
  }

  // Non-object payloads (arrays, strings, numbers) have no key/value shape —
  // show them as pretty-printed JSON instead.
  if (
    typeof parsedData !== 'object' ||
    parsedData === null ||
    Array.isArray(parsedData)
  ) {
    return (
      <div className="p-3 bg-default-100 dark:bg-default-50 rounded-lg">
        <pre className="whitespace-pre-wrap break-all font-mono text-xs text-default-600 m-0">
          {JSON.stringify(parsedData, null, 2)}
        </pre>
      </div>
    );
  }

  const entries = Object.entries(parsedData);

  if (entries.length === 0) {
    return <EmptyMetaDataState />;
  }

  return (
    <div className="bg-content1 rounded-lg border border-default-200 max-h-96 overflow-auto">
      <table className="w-full text-sm">
        <thead className="bg-default-100 dark:bg-default-50 sticky top-0">
          <tr>
            <th className="text-left p-3 font-semibold text-foreground border-b border-default-200">
              Key
            </th>
            <th className="text-left p-3 font-semibold text-foreground border-b border-default-200">
              Value
            </th>
          </tr>
        </thead>
        <tbody>
          {entries.map(([key, value]) => (
            <tr
              key={key}
              className="border-b border-default-200 last:border-b-0 hover:bg-default-50"
            >
              <td className="p-3 font-medium text-foreground align-top">
                {key}
              </td>
              <td className="p-3 text-default-600 break-all">
                {renderValue(value)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
